// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/PlanejamentoMrp.tsx
//  Planejamento de necessidades de materiais (MRP) — paridade FrmPlanejamentoProducao.
//  Plano de produção → explosão de BOM → necessidade líquida vs. saldo de estoque.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Calculator, Trash2, AlertTriangle, PackagePlus, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { mrpService, type MrpDemanda, type MrpResultado } from '@/features/operacional/services/mrpService';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { ProdutoSearchCombobox } from '@/components/ProdutoSearchCombobox';
import { ProdutoNome } from '@/components/ProdutoNome';
import { FiltroPeriodo } from '@/components/FiltroPeriodo';

interface DemandaRow {
  _key: number;
  produtoId: number;
  quantidade: number;
}

const fmt = (v: number) => (v ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });

export default function PlanejamentoMrp() {
  const navigate = useNavigate();
  const [demandas, setDemandas] = useState<DemandaRow[]>([{ _key: 1, produtoId: 0, quantidade: 1 }]);
  const [nextKey, setNextKey]   = useState(2);
  const [resultado, setResultado] = useState<MrpResultado | null>(null);

  const addRow = () => setNextKey((k) => { setDemandas((p) => [...p, { _key: k, produtoId: 0, quantidade: 1 }]); return k + 1; });
  const removeRow = (key: number) => setDemandas((p) => p.filter((r) => r._key !== key));
  const updRow = (key: number, field: 'produtoId' | 'quantidade', value: number) =>
    setDemandas((p) => p.map((r) => (r._key === key ? { ...r, [field]: value } : r)));

  // Importações SOMAM ao plano: produto já presente acumula a quantidade; linhas vazias saem.
  const somarAoPlano = (lista: MrpDemanda[]) => {
    setDemandas((atual) => {
      const plano = atual.filter((d) => d.produtoId > 0).map((d) => ({ ...d }));
      let k = Math.max(0, ...atual.map((d) => d._key)) + 1;
      for (const item of lista) {
        const existente = plano.find((d) => d.produtoId === item.produtoId);
        if (existente) existente.quantidade += item.quantidade;
        else plano.push({ _key: k++, produtoId: item.produtoId, quantidade: item.quantidade });
      }
      setNextKey(k);
      return plano;
    });
    setResultado(null);
  };

  // Ordens de produção em aberto: o que falta produzir (prev. entrega no período, opcional).
  const [periodo, setPeriodo] = useState({ inicio: '', fim: '' });
  const importarMutation = useMutation({
    mutationFn: () => mrpService.demandaOrdensAbertas(periodo.inicio, periodo.fim),
    onSuccess: (lista) => {
      if (lista.length === 0) {
        toast.info('Nenhuma ordem de produção em aberto com saldo a produzir no período');
        return;
      }
      somarAoPlano(lista);
      toast.success(`${lista.length} produto(s) somado(s) das ordens em aberto`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  // Pedidos de venda (DAVs) em aberto dos últimos N dias — só produtos com ficha técnica (fabricados).
  const [diasPedidos, setDiasPedidos] = useState(30);
  const [somenteComFicha, setSomenteComFicha] = useState(true);
  const importarPedidosMutation = useMutation({
    mutationFn: () => mrpService.demandaPedidosAbertos(diasPedidos, somenteComFicha),
    onSuccess: (lista) => {
      if (lista.length === 0) {
        toast.info(`Nenhum pedido em aberto nos últimos ${diasPedidos} dias${somenteComFicha ? ' com produto fabricado (ficha técnica)' : ''}`);
        return;
      }
      somarAoPlano(lista);
      toast.success(`${lista.length} produto(s) somado(s) dos pedidos em aberto`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const calcMutation = useMutation({
    mutationFn: () => {
      const validas = demandas.filter((d) => d.produtoId > 0 && d.quantidade > 0);
      if (validas.length === 0) throw new Error('Adicione ao menos um produto e quantidade');
      return mrpService.calcular(validas.map(({ produtoId, quantidade }) => ({ produtoId, quantidade })));
    },
    onSuccess: (res) => {
      setResultado(res);
      if (res.necessidades.length === 0) toast.info('Nenhuma necessidade de material apurada');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Calculator className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Planejamento de Produção (MRP)</h1>
          <p className="text-sm text-muted-foreground">
            Calcule a necessidade de insumos para um plano de produção, líquida do saldo em estoque.
          </p>
        </div>
      </div>

      {/* Plano de produção */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Plano de Produção</CardTitle>
            <Button size="sm" variant="outline" className="gap-2" onClick={addRow}>
              <Plus className="h-4 w-4" />Adicionar Produto
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-2 pt-2">
            <FiltroPeriodo
              rotulo="Ordens em aberto — prev. entrega"
              inicio={periodo.inicio}
              fim={periodo.fim}
              onChange={setPeriodo}
            />
            <Button size="sm" variant="secondary" className="gap-2 h-9"
              onClick={() => importarMutation.mutate()} disabled={importarMutation.isPending}>
              <Download className="h-4 w-4" />
              {importarMutation.isPending ? 'Importando...' : 'Importar das ordens em aberto'}
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-3 pt-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Pedidos em aberto — últimos (dias)</Label>
              <Input type="number" min={0} className="h-9 w-24" value={diasPedidos}
                onChange={(e) => setDiasPedidos(Math.max(0, Number(e.target.value)))} />
            </div>
            <label className="flex items-center gap-2 text-sm h-9">
              <Checkbox checked={somenteComFicha} onCheckedChange={(v) => setSomenteComFicha(v === true)} />
              Só produtos fabricados (com ficha técnica)
            </label>
            <Button size="sm" variant="secondary" className="gap-2 h-9"
              onClick={() => importarPedidosMutation.mutate()} disabled={importarPedidosMutation.isPending}>
              <Download className="h-4 w-4" />
              {importarPedidosMutation.isPending ? 'Importando...' : 'Importar dos pedidos em aberto'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            As importações somam ao plano. Pedido em aberto = DAV não baixado nem estornado, que não é OS.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[260px]">Produto Acabado</TableHead>
                <TableHead className="text-right">Quantidade</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {demandas.map((d) => (
                <TableRow key={d._key}>
                  <TableCell>
                    <ProdutoSearchCombobox
                      value={d.produtoId || undefined}
                      onChange={(id) => updRow(d._key, 'produtoId', id)}
                      placeholder="Buscar produto..."
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input type="number" min={0} step="any" className="w-28 text-right ml-auto"
                      value={d.quantidade}
                      onChange={(e) => updRow(d._key, 'quantidade', Number(e.target.value))} />
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => removeRow(d._key)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-end">
            <Button className="gap-2" onClick={() => calcMutation.mutate()} disabled={calcMutation.isPending}>
              <Calculator className="h-4 w-4" />{calcMutation.isPending ? 'Calculando...' : 'Calcular Necessidades'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resultado */}
      {resultado && (
        <>
          {resultado.produtosSemFicha.length > 0 && (
            <Card className="border-yellow-500/40">
              <CardContent className="pt-4 flex items-start gap-2 text-sm">
                <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
                <div>
                  <p className="font-medium text-yellow-700">Produtos sem ficha técnica/composição</p>
                  <p className="text-muted-foreground">
                    Os produtos abaixo não têm BOM cadastrado e foram ignorados na explosão.
                    Cadastre a ficha técnica para incluí-los.
                  </p>
                  <ul className="mt-1 list-disc pl-5">
                    {resultado.produtosSemFicha.map((id) => (
                      <li key={id}><ProdutoNome id={id} /></li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Necessidade de Materiais</CardTitle>
            </CardHeader>
            <CardContent>
              {resultado.necessidades.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">Nenhuma necessidade apurada.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Matéria-prima</TableHead>
                      <TableHead className="text-right">Necessidade Bruta</TableHead>
                      <TableHead className="text-right">Saldo Disponível</TableHead>
                      <TableHead className="text-right">Necessidade Líquida</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resultado.necessidades.map((n) => (
                      <TableRow key={n.materiaPrimaId} className={n.necessidadeLiquida > 0 ? 'bg-red-500/5' : undefined}>
                        <TableCell className="font-medium"><ProdutoNome id={n.materiaPrimaId} /></TableCell>
                        <TableCell className="text-right">{fmt(n.necessidadeBruta)}</TableCell>
                        <TableCell className="text-right">{fmt(n.saldoDisponivel)}</TableCell>
                        <TableCell className="text-right font-bold">
                          {n.necessidadeLiquida > 0
                            ? <span className="text-red-600">{fmt(n.necessidadeLiquida)}</span>
                            : <span className="text-green-600">0</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          {n.necessidadeLiquida > 0 && (
                            <Button size="sm" variant="outline" className="gap-1"
                              onClick={() => navigate('/compras/requisicoes')}
                              title="Gerar requisição de compra">
                              <PackagePlus className="h-4 w-4" />Comprar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
